import {
  COACHING_FOCUS_LABELS,
  COACHING_STYLES,
  type CoachingFocusCategory,
  type CoachingStyle,
} from '@mlm/shared/coaching';
import type { Action, ChartResponse } from './api';

const [MONTHS, DAYS] = [
  ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
];

export function greeting(at = new Date()): string {
  const h = at.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

/** `YYYY-MM-DD` → `Tue 15 Sep` (calendar date, no timezone shift). */
export function shortDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${DAYS[date.getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
}

/** `YYYY-MM-DD` → `15 Sep 2026`. */
export function longDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/**
 * An ISO instant → `15 Sep 2026` on the member's OWN calendar (the browser's
 * time zone), never UTC's: a window that ends at 20:42Z on the 5th is the 6th
 * in Manila, and Stripe's receipt says the 6th.
 */
export function instantDay(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return longDay(iso.slice(0, 10));
  return `${at.getDate()} ${MONTHS[at.getMonth()]} ${at.getFullYear()}`;
}

export function focusLabel(key: string | null | undefined): string | null {
  return key && key in COACHING_FOCUS_LABELS
    ? COACHING_FOCUS_LABELS[key as CoachingFocusCategory]
    : null;
}

export function styleLabel(key: string | null | undefined): string | null {
  return key && key in COACHING_STYLES ? COACHING_STYLES[key as CoachingStyle].label : null;
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export function minutesLabel(minutes: number | null): string | null {
  return minutes ? `${minutes} min` : null;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Manila';
  } catch {
    return 'Asia/Manila';
  }
}

/** `Priority 1 · 30 min · Partly done` */
export const actionMeta = (a: Action) =>
  [
    `Priority ${a.priority}`,
    minutesLabel(a.estimatedMinutes),
    a.status === 'partial' ? 'Partly done' : null,
    a.status === 'skipped' ? 'Skipped' : null,
  ]
    .filter(Boolean)
    .join(' · ');

export const chartTitle = (data: ChartResponse) =>
  data.chart.ascendant ? `${data.chart.ascendant.sign} rising` : 'Your Moon chart';

/** `Moon in Sagittarius, Purva Ashadha nakshatra, pada 4` */
export function chartMoonLine(data: ChartResponse) {
  const c = data.chart;
  const moon = c.planets.find((p) => p.name === 'Moon');
  return `Moon in ${moon?.sign ?? ''}, ${c.moonNakshatra.name} nakshatra, pada ${c.moonNakshatra.pada}`;
}

/** Seconds → `4:05`, or `1:04:05` past an hour. */
export function clockLabel(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** Seconds → `4 min` (rounded up to the minute, never `0 min`). */
export function durationLabel(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  return `${Math.max(1, Math.round(seconds / 60))} min`;
}
